import axios from "axios";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Moderation (`/api/admin/comments`, `/api/admin/reports`). Comments and
 * reports are both stored inside their post, so each is addressed by the
 * post's id as well as its own.
 *
 * Each call resolves to the response's `data` and throws with the server's own
 * message — a 403 names the permission that is missing, and that wording has
 * to reach the person as written.
 */
const call = async (request) => {
  try {
    const { data } = await request;
    if (data?.status === false) throw new Error(data.error || "Something went wrong");
    return data?.data;
  } catch (error) {
    throw new Error(error?.response?.data?.error || error.message || "Something went wrong");
  }
};

export const commentsApi = (token) => {
  const opts = { headers: { Authorization: `Bearer ${token}` } };
  return {
    list: (params) => call(axios.get(`${API}/api/admin/comments`, { ...opts, params })),
    remove: (postId, commentId) =>
      call(axios.delete(`${API}/api/admin/comment/${postId}/${commentId}`, opts)),
    // Bulk: PUT, because what to remove travels in the body — the shape the
    // panel's other bulk deletes use.
    removeMany: (comments) =>
      call(axios.put(`${API}/api/admin/comments/delete`, { comments }, opts)),

    post: (postId) =>
      call(axios.get(`${API}/api/admin/moderation/post/${postId}`, opts)),

    reports: (params) => call(axios.get(`${API}/api/admin/reports`, { ...opts, params })),
    setReportStatus: (postId, status) =>
      call(axios.put(`${API}/api/admin/reports/${postId}`, { status }, opts)),
    setCommentReportStatus: (postId, commentId, status) =>
      call(axios.put(`${API}/api/admin/reports/${postId}/comment/${commentId}`, { status }, opts)),

    feedback: (params) =>
      call(axios.get(`${API}/api/admin/summary-feedback`, { ...opts, params })),
  };
};

/**
 * "21 Sep 2026, 5:50 PM", in India time.
 *
 * Pinned to IST rather than the browser's zone: the team reads IST, the CSV
 * exports are formatted in IST on the server, and a moderator opening the
 * panel abroad must not see a report land at a different time than everyone
 * else does. Assembled from parts rather than a locale's whole string, because
 * that string changes under us — en-GB now spells September "Sept".
 */
const WHEN_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

export const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const part = Object.fromEntries(
    WHEN_FORMAT.formatToParts(date).map(({ type, value: v }) => [type, v])
  );
  return `${part.day} ${part.month} ${part.year}, ${part.hour}:${part.minute} ${part.dayPeriod}`;
};
